import logger from '../../utils/logger.js';
import { API_BASE_URL } from '../../config/api.config';

const normalizeRole = (role, organizationType) => {
  const raw = `${role || organizationType || ''}`.trim().toLowerCase();
  if (!raw) return 'gc';
  if (['admin', 'administrator'].includes(raw)) return 'admin';
  if (['worker'].includes(raw)) return 'worker';
  if (['foreman'].includes(raw)) return 'foreman';
  if (['safety_supervisor', 'safetysupervisor', 'safety-supervisor'].includes(raw)) return 'safety_supervisor';
  if (['auditor', 'consultant'].includes(raw)) return 'auditor';
  if (
    ['gc', 'gcadmin', 'contractor', 'general', 'general-contractor'].includes(raw)
  ) {
    return 'gcAdmin';
  }
  if (
    ['sc', 'scadmin', 'subcontractor', 'sub-contractor'].includes(raw)
  ) {
    return 'scAdmin';
  }
  return 'gcAdmin';
};

const normalizeOrganizationType = (organizationType, role) => {
  const raw = `${organizationType || role || ''}`.trim().toLowerCase();
  if (!raw) return undefined;
  if (
    ['gc', 'gcadmin', 'contractor', 'general', 'general-contractor'].includes(raw)
  ) {
    return 'gc';
  }
  if (['sc', 'scadmin'].includes(raw)) return 'sc';
  if (['subcontractor', 'sub-contractor'].includes(raw)) return 'subcontractor';
  if (['consultant', 'auditor'].includes(raw)) return raw;
  return undefined;
};

const normalizePhone = (phone) => {
  const value = `${phone || ''}`.trim();
  if (!value) return undefined;
  return /^\+?[1-9]\d{1,14}$/.test(value) ? value : undefined;
};

/** Detect network/fetch failures and return a user-friendly message */
const toUserFriendlyError = (error) => {
  const msg = (error?.message || String(error)).toLowerCase();
  if (
    msg.includes('failed to fetch') ||
    msg.includes('load failed') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed')
  ) {
    return new Error(
      `Cannot connect to the API. Make sure the backend is running (e.g. cd vh-api && npm run dev:local).`
    );
  }
  return error;
};

class JWTAdapter {
  constructor() {
    this.currentUser = null;
    this.subscribers = [];
    this.token = null;
    this.refreshToken = null;
    this._refreshInterval = null;

    // Unified API base URL (no /api suffix)
    this.baseURL = API_BASE_URL;

    // Initialize with stored token if exists
    this.initializeFromStorage();
  }

  initializeFromStorage() {
    try {
      const token = localStorage.getItem('vh_auth_token');
      const refreshToken = localStorage.getItem('vh_refresh_token');
      const user = localStorage.getItem('vh_user_data');

      logger.info('[JWTAdapter] Initializing from storage:', { 
        hasToken: !!token, 
        hasRefreshToken: !!refreshToken, 
        hasUser: !!user 
      });

      if (token && user) {
        this.token = token;
        this.refreshToken = refreshToken;
        this.currentUser = JSON.parse(user);
        // Ensure organizationId is populated from localStorage if not in stored user
        if (!this.currentUser.organizationId) {
          const storedOrgId = localStorage.getItem('organizationId');
          if (storedOrgId) this.currentUser.organizationId = storedOrgId;
        }
        logger.info('[JWTAdapter] Restored user from storage:', this.currentUser?.email);
        this._startAutoRefresh();
        this._notifySubscribers(this.currentUser);
        // Login payload is minimal; refresh profile so Foreman (and similar) get
        // currentMembership + populated jobsites from GET /api/auth/me.
        void this._mergeProfileFromMe();
      } else {
        logger.info('[JWTAdapter] No stored auth found');
      }
    } catch (error) {
      // TEMPORARILY: Don't clear auth on parse errors
      logger.error('[JWTAdapter] Error initializing from storage (NOT clearing auth):', {
        error: error
      });
      // Removed: this._clearStoredAuth();
    }
  }

  async signIn(email, password) {
    try {
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });
      } catch (fetchError) {
        throw toUserFriendlyError(fetchError);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = {};
        }
        throw new Error(errorData.error || errorData.message || 'Login failed');
      }

      const data = await response.json();

      // Backend returns nested format: { success: true, data: { accessToken, refreshToken, user } }
      // Handle both nested and flat formats for compatibility
      const responseData = data.data || data;
      this.token = responseData.accessToken || responseData.token || data.accessToken || data.token;
      this.refreshToken = responseData.refreshToken || data.refreshToken;
      this.currentUser = responseData.user || data.user;

      // Extract organizationId from user data (API may return populated org object or string)
      const organizationId = this._extractOrganizationId(this.currentUser);
      if (organizationId) {
        this.currentUser.organizationId = organizationId;
        localStorage.setItem('organizationId', organizationId);
      }
      if (this.currentUser?.organization?.name && !this.currentUser.organizationName) {
        this.currentUser.organizationName = this.currentUser.organization.name;
      }

      // Store auth data with both keys for compatibility
      localStorage.setItem('vh_auth_token', this.token);
      localStorage.setItem('access_token', this.token);
      if (this.refreshToken) {
        localStorage.setItem('vh_refresh_token', this.refreshToken);
        localStorage.setItem('refresh_token', this.refreshToken);
      }
      localStorage.setItem('vh_user_data', JSON.stringify(this.currentUser));

      // Start auto-refresh (12-min cycle for 15-min JWT TTL)
      this._startAutoRefresh();

      const hydrated = await this._mergeProfileFromMe();
      if (!hydrated) this._notifySubscribers(this.currentUser);
      return this.currentUser;
    } catch (error) {
      logger.error('JWT signIn error:', { error: error });
      throw error;
    }
  }

  async register(email, password, userData = {}) {
    try {
      // Map frontend userData to backend expected format
      const normalizedRole = normalizeRole(userData.role, userData.organizationType);
      const normalizedOrganizationType = normalizeOrganizationType(
        userData.organizationType,
        userData.role
      );
      const normalizedPhone = normalizePhone(userData.phone);
      
      // Determine accountType from role/organizationType
      // organization = GC/SC admin, otherwise use the role directly
      const accountType = userData.accountType || (
        ['gcAdmin', 'scAdmin', 'gc', 'sc'].includes(normalizedRole) 
          ? 'organization' 
          : normalizedRole
      );
      
      const requestData = {
        email,
        password,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        role: normalizedRole,
        accountType: accountType,
        organizationName: userData.organizationName || '',
        referralCode: userData.inviteCode || userData.referralCode || '',
      };
      if (normalizedPhone) {
        requestData.phone = normalizedPhone;
      }
      if (normalizedOrganizationType) {
        requestData.organizationType = normalizedOrganizationType;
      }

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
      } catch (fetchError) {
        throw toUserFriendlyError(fetchError);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = {};
        }
        const registrationError = new Error(
          errorData.error || errorData.message || 'Registration failed'
        );
        // Preserve the HTTP status and any field-level details from the
        // validation middleware ({message, errors: [{field, message}]}) so
        // the signup form can render honest, per-field messages.
        registrationError.status = response.status;
        if (Array.isArray(errorData.errors)) {
          registrationError.errors = errorData.errors;
        }
        throw registrationError;
      }

      const data = await response.json();

      // Backend returns nested format: { success: true, data: { accessToken, refreshToken, user } }
      // Handle both nested and flat formats for compatibility
      const responseData = data.data || data;
      this.token = responseData.accessToken || responseData.token || data.accessToken || data.token;
      this.refreshToken = responseData.refreshToken || data.refreshToken;
      this.currentUser = responseData.user || data.user;

      // Store auth data with both keys for compatibility
      localStorage.setItem('vh_auth_token', this.token);
      localStorage.setItem('access_token', this.token);
      if (this.refreshToken) {
        localStorage.setItem('vh_refresh_token', this.refreshToken);
        localStorage.setItem('refresh_token', this.refreshToken);
      }
      localStorage.setItem('vh_user_data', JSON.stringify(this.currentUser));

      // Store onboarding requirement
      const requiresOnboarding = responseData.requiresOnboarding || data.requiresOnboarding;
      if (requiresOnboarding) {
        localStorage.setItem('vh_requires_onboarding', 'true');
      }

      const hydrated = await this._mergeProfileFromMe();
      if (!hydrated) this._notifySubscribers(this.currentUser);
      return this.currentUser;
    } catch (error) {
      logger.error('JWT registration error:', { error: error });
      throw error;
    }
  }

  async signOut() {
    try {
      // Call backend logout if we have a token
      if (this.token) {
        await fetch(`${this.baseURL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      logger.error('Error during backend logout:', { error: error });
      // Continue with local logout even if backend call fails
    } finally {
      // Stop auto-refresh and clear local state
      this._stopAutoRefresh();
      this.token = null;
      this.refreshToken = null;
      this.currentUser = null;

      this._clearStoredAuth();
      this._notifySubscribers(null);
    }
  }

  subscribe(onUserChange, setRoles) {
    const subscriber = { onUserChange, setRoles };
    this.subscribers.push(subscriber);

    // Immediately notify with current state
    this._notifySubscriber(subscriber, this.currentUser);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter((sub) => sub !== subscriber);
    };
  }

  async refreshAuthToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseURL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Backend returns nested format: { success: true, data: { accessToken, refreshToken } }
      // Handle both nested and flat formats for compatibility
      const responseData = data.data || data;
      this.token = responseData.accessToken || responseData.token || data.accessToken || data.token;
      if (responseData.refreshToken || data.refreshToken) {
        this.refreshToken = responseData.refreshToken || data.refreshToken;
      }

      // Update stored tokens with both keys for compatibility
      localStorage.setItem('vh_auth_token', this.token);
      localStorage.setItem('access_token', this.token);
      if (this.refreshToken) {
        localStorage.setItem('vh_refresh_token', this.refreshToken);
        localStorage.setItem('refresh_token', this.refreshToken);
      }

      return this.token;
    } catch (error) {
      // TEMPORARILY DISABLED: Auto-logout on refresh failure
      // This was causing issues - just log and continue
      logger.warn('Token refresh failed - NOT signing out (disabled for debugging):', {
        error: error.message,
      });
      throw error;
    }
  }

  async refreshProfileFromMe() {
    return this._mergeProfileFromMe();
  }

  /**
   * Merge GET /api/auth/me into currentUser (memberships, currentMembership,
   * currentJobsite, organization). Notifies subscribers on success.
   * @returns {Promise<boolean>} true if merge ran and subscribers were notified
   */
  async _mergeProfileFromMe() {
    if (!this.token) return false;
    try {
      const response = await fetch(`${this.baseURL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        logger.warn('[JWTAdapter] GET /api/auth/me failed during profile merge', {
          status: response.status,
        });
        return false;
      }
      const payload = await response.json();
      const me = payload.data || payload;
      if (!me || typeof me !== 'object') return false;

      this.currentUser = {
        ...this.currentUser,
        ...me,
        organizationId:
          this._extractOrganizationId({ ...this.currentUser, ...me }) ||
          this.currentUser?.organizationId ||
          null,
      };
      if (this.currentUser?.organization?.name && !this.currentUser.organizationName) {
        this.currentUser.organizationName = this.currentUser.organization.name;
      }
      localStorage.setItem('vh_user_data', JSON.stringify(this.currentUser));
      const storedOrg = this._extractOrganizationId(this.currentUser);
      if (storedOrg) localStorage.setItem('organizationId', storedOrg);

      this._notifySubscribers(this.currentUser);
      return true;
    } catch (error) {
      logger.warn('[JWTAdapter] Profile merge from /me failed', { error: error.message });
      return false;
    }
  }

  /**
   * Extract organizationId as a string from user data.
   * Handles: string, ObjectId, populated Organization doc, nested organization._id
   */
  _extractOrganizationId(user) {
    if (!user) return null;
    // Direct organizationId field (string or ObjectId)
    if (user.organizationId != null) {
      if (typeof user.organizationId === 'object' && user.organizationId !== null) {
        return (user.organizationId._id ?? user.organizationId.id)?.toString() ?? null;
      }
      return String(user.organizationId);
    }
    // Fallback: populated organization object
    if (user.organization?._id) return user.organization._id.toString();
    if (user.organization?.id) return user.organization.id.toString();
    // Fallback: localStorage (set by other auth flows)
    const stored = localStorage.getItem('organizationId');
    if (stored) return stored;
    return null;
  }

  /**
   * Auto-refresh JWT token on a 12-minute interval (tokens have 15-min TTL).
   * Prevents stale tokens in long-lived sessions (e.g., BOLT chat).
   */
  _startAutoRefresh() {
    this._stopAutoRefresh();
    // Refresh every 12 minutes (720000ms) — well within 15-min TTL
    this._refreshInterval = setInterval(async () => {
      try {
        logger.info('[JWTAdapter] Auto-refreshing token');
        await this.refreshAuthToken();
        logger.info('[JWTAdapter] Token auto-refreshed successfully');
      } catch (error) {
        logger.warn('[JWTAdapter] Auto-refresh failed:', { error: error.message });
        // Don't sign out — let the next API call handle 401
      }
    }, 12 * 60 * 1000);
  }

  _stopAutoRefresh() {
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  }

  _clearStoredAuth() {
    localStorage.removeItem('vh_auth_token');
    localStorage.removeItem('vh_refresh_token');
    localStorage.removeItem('vh_user_data');
    localStorage.removeItem('vh_requires_onboarding');
    localStorage.removeItem('organizationId');
  }

  _notifySubscribers(user) {
    this.subscribers.forEach((subscriber) => {
      this._notifySubscriber(subscriber, user);
    });
  }

  _notifySubscriber(subscriber, user) {
    try {
      if (typeof subscriber.onUserChange === 'function') {
        subscriber.onUserChange(user);
      }
      if (typeof subscriber.setRoles === 'function') {
        const roles = user ? [user.role] : [];
        subscriber.setRoles(roles);
      }
    } catch (error) {
      logger.error('Error notifying auth subscriber:', { error: error });
    }
  }

  // Helper method to get current token for API calls
  getToken() {
    return this.token;
  }

  // Helper method to check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.currentUser;
  }
}

const jwtAdapter = new JWTAdapter();
export default jwtAdapter;
